import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, formatKisa, formatTarih, formatTl } from '../lib/api';
import { useAuth } from '../lib/auth';
import AraSec from '../ui/AraSec';
import Ipucu from '../ui/Ipucu';
import KpiKart, { KpiIzgara } from '../ui/KpiKart';
import {
  EmanetDokumCekmece, HareketCekmece, HesapCekmece, IptalCekmece, KategoriCekmece, Secim, VirmanCekmece, type HareketOn,
} from './kasa/Cekmeceler';
import {
  TUR_AD, YON_AD, gunYaz, hesapSecenek, ibanYaz, kasaCsvIndir, paraYaz, useKasaTanimlari,
  type EmanetSatiri, type EmanetToplam, type Hareket, type Hesap, type HesapTur, type Kategori, type Ozet, type Para,
} from './kasa/ortak';

/**
 * KASA VE BANKA — kasa, banka ve emanet hesaplarının defteri (docs/KASA-BANKA.md).
 *
 * Dört sekme: Özet · Hareketler · Emanetler · Tanımlar. Kurallar sunucuda; gayriresmî
 * hesaplar bu ekrana yalnız `informal.view` yetkisi olan rolde GELİR (sunucu süzer),
 * arayüz ayrıca bir şey gizlemez.
 */
const SEKMELER = [['ozet', 'Özet'], ['hareketler', 'Hareketler'], ['emanetler', 'Emanetler'], ['tanimlar', 'Tanımlar']] as const;
type Sekme = (typeof SEKMELER)[number][0];

type Cekmece =
  | { tur: 'hareket'; on?: HareketOn }
  | { tur: 'virman'; kaynakId?: number }
  | { tur: 'hesap'; hesap: Hesap | null }
  | { tur: 'kategori'; kategori: Kategori | null }
  | { tur: 'iptal'; hareket: Hareket }
  | { tur: 'emanet'; satir: EmanetSatiri };

/** Hesapların bakiyesini para birimi bazında toplar (TRY önde). */
function paraToplam(hs: Hesap[]): { para: Para; kurus: number }[] {
  const m = new Map<Para, number>();
  for (const h of hs) m.set(h.para, (m.get(h.para) ?? 0) + h.bakiyeKurus);
  return [...m.entries()].map(([para, kurus]) => ({ para, kurus })).sort((a, b) => (a.para === 'TRY' ? -1 : b.para === 'TRY' ? 1 : a.para.localeCompare(b.para)));
}
const tl = (t: { para: Para; kurus: number }[]) => t.find((x) => x.para === 'TRY')?.kurus ?? 0;
const dovizSatiri = (t: { para: Para; kurus: number }[]) => t.filter((x) => x.para !== 'TRY' && x.kurus !== 0).map((x) => paraYaz(x.kurus, x.para)).join(' · ');

/** `yalnizTanim`: Ayarlar › Kasa ve banka tanımları bölümü; başlık ve sekmeler çizilmez, yalnız Tanımlar görünür. */
export default function Kasa({ yalnizTanim }: { yalnizTanim?: boolean } = {}) {
  const { can } = useAuth();
  const yazar = can('cash.write');
  const tanimlar = can('cash.settings');
  const [ara, setAra] = useSearchParams();
  const sekme = (yalnizTanim ? 'tanimlar' : SEKMELER.find(([id]) => id === ara.get('sekme'))?.[0] ?? 'ozet') as Sekme;
  const sekmeAc = (s: Sekme, ek: Record<string, string> = {}) => setAra({ ...(s === 'ozet' ? {} : { sekme: s }), ...ek }, { replace: true });

  const [ozet, setOzet] = useState<Ozet | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [cekmece, setCekmece] = useState<Cekmece | null>(null);
  /** Artınca sekmeler verisini yeniden okur. */
  const [surum, setSurum] = useState(0);
  const t = useKasaTanimlari();

  const ozetYukle = useCallback(() => api<Ozet>('/api/kasa/ozet').then((r) => { setOzet(r); setHata(null); }).catch((e) => setHata((e as Error).message)), []);
  useEffect(() => { void ozetYukle(); }, [ozetYukle]);
  const yenile = useCallback(async () => { await Promise.all([ozetYukle(), t.yukle()]); setSurum((n) => n + 1); }, [ozetYukle, t]);
  const oldu = (mesaj: string) => { setBilgi(mesaj); setHata(null); void yenile(); };

  const hesaplar = t.hesaplar ?? [];
  const kategoriler = t.kategoriler ?? [];
  const hesapYok = t.hesaplar != null && t.hesaplar.length === 0;

  return (
    <>
      {!yalnizTanim && <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <h1 className="ccd-title">Kasa ve Banka</h1>
          <div className="ccd-meta">Kasa, banka ve emanet hesaplarınız: günlük para girişi ve çıkışı, hesaplar arası aktarım, kimin parası kimde.</div>
        </div>
        {yazar && (
          <div className="ccd-row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button className="ccd-btn ccd-btn-sm" onClick={() => setCekmece({ tur: 'virman' })} disabled={hesaplar.filter((h) => h.aktif).length < 2}
                    title={hesaplar.filter((h) => h.aktif).length < 2 ? 'Virman için en az iki etkin hesap gerekir' : 'Bir hesaptan öbürüne para aktar'} data-tur="kasa-virman">⇄ Virman</button>
            <button className="ccd-btn ccd-btn-primary ccd-btn-sm" onClick={() => setCekmece({ tur: 'hareket' })} data-tur="kasa-yeni-hareket">＋ Yeni hareket</button>
          </div>
        )}
      </div>}

      {!yalnizTanim && <div className="ccd-kasa-bilgi" role="note">
        <span aria-hidden="true">ⓘ</span>
        <span>
          Bu defter <b>günlük nakit{ozet?.gayriGorur ? ', gayriresmî kasa' : ''} ve emanet takibi</b> içindir. Resmî tahsilat ve ödemelerin asıl kaydı {ozet?.muhasebeAd ?? 'muhasebe programı'}'tedir;
          panel oraya <b>hiçbir şey yazmaz</b>, yalnız hesap bakiyelerini okuyup yan yana gösterir. <Ipucu id="mutabakat-farki" />
        </span>
      </div>}

      {bilgi && <div className="ccd-banner" role="status"><span className="ccd-banner-icon" aria-hidden="true">✓</span><span>{bilgi}</span></div>}
      {(hata || t.hata) && <div className="ccd-banner ccd-banner-danger" role="alert"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata ?? t.hata}</span></div>}

      {!yalnizTanim && <div className="ccd-tabs" role="tablist" data-tur="kasa-sekmeler">
        {SEKMELER.map(([id, ad]) => (
          <button key={id} role="tab" aria-selected={sekme === id} className="ccd-tab" onClick={() => { setBilgi(null); sekmeAc(id); }}>{ad}</button>
        ))}
      </div>}

      {hesapYok && sekme !== 'tanimlar' ? (
        <div className="ccd-empty">
          <b>Henüz kasa ya da banka hesabı tanımlı değil.</b>
          <div style={{ marginTop: 6 }}>Önce paranın durduğu yerleri tanımlayın: merkez kasa, banka hesapları, varsa gayriresmî kasa.</div>
          {tanimlar
            ? <div className="ccd-row" style={{ gap: 8, justifyContent: 'center', marginTop: 12, flexWrap: 'wrap' }}>
                <button className="ccd-btn ccd-btn-primary" onClick={() => setCekmece({ tur: 'hesap', hesap: null })}>＋ Hesap tanımla</button>
                <button className="ccd-btn" onClick={() => sekmeAc('tanimlar')}>Tanımlar sekmesine git</button>
              </div>
            : <div className="ccd-meta" style={{ marginTop: 8 }}>Hesap tanımlamak için yöneticinize başvurun.</div>}
        </div>
      ) : (
        <>
          {sekme === 'ozet' && <OzetSekmesi ozet={ozet} yazar={yazar}
            hareketlereGit={(id) => sekmeAc('hareketler', { hesap: String(id) })}
            emanetlereGit={() => sekmeAc('emanetler')}
            yeniHareket={(id) => setCekmece({ tur: 'hareket', on: { hesapId: id } })} />}
          {sekme === 'hareketler' && <HareketSekmesi surum={surum} hesaplar={hesaplar} kategoriler={kategoriler} yazar={yazar}
            ilkHesap={ara.get('hesap') ?? ''} iptalAc={(h) => setCekmece({ tur: 'iptal', hareket: h })} />}
          {sekme === 'emanetler' && <EmanetSekmesi surum={surum} dokumAc={(s) => setCekmece({ tur: 'emanet', satir: s })}
            yeniHareket={yazar ? (on) => setCekmece({ tur: 'hareket', on }) : undefined} />}
        </>
      )}
      {sekme === 'tanimlar' && <TanimSekmesi ozet={ozet} hesaplar={hesaplar} kategoriler={kategoriler} yukleniyor={t.hesaplar == null}
        hesapAc={(h) => setCekmece({ tur: 'hesap', hesap: h })} kategoriAc={(k) => setCekmece({ tur: 'kategori', kategori: k })} oldu={oldu} hataVer={setHata} />}

      {cekmece?.tur === 'hareket' && (
        <HareketCekmece hesaplar={hesaplar} kategoriler={kategoriler} gruplar={t.gruplar} on={cekmece.on}
                        kapat={() => setCekmece(null)} kaydedildi={oldu} tanimYenile={t.yukle}
                        hesapTanimla={tanimlar ? () => setCekmece({ tur: 'hesap', hesap: null }) : undefined} />
      )}
      {cekmece?.tur === 'virman' && <VirmanCekmece hesaplar={hesaplar} on={{ kaynakId: cekmece.kaynakId }} kapat={() => setCekmece(null)} kaydedildi={oldu} />}
      {cekmece?.tur === 'iptal' && <IptalCekmece hareket={cekmece.hareket} kapat={() => setCekmece(null)} iptalEdildi={oldu} />}
      {cekmece?.tur === 'hesap' && ozet && (
        <HesapCekmece hesap={cekmece.hesap} gayriGorur={ozet.gayriGorur} muhasebeAd={ozet.muhasebeAd} muhasebeKip={ozet.muhasebeKip}
                      kapat={() => setCekmece(null)} kaydedildi={oldu} />
      )}
      {cekmece?.tur === 'kategori' && (
        <KategoriCekmece kategori={cekmece.kategori} gruplar={t.gruplar} kapat={() => setCekmece(null)}
                         kaydedildi={(_k, mesaj) => { setCekmece(null); oldu(mesaj); }} />
      )}
      {cekmece?.tur === 'emanet' && (
        <EmanetDokumCekmece baslik={cekmece.satir.ad} anahtar={cekmece.satir.anahtar} kapat={() => setCekmece(null)}
                            ozet={<div className="ccd-kasa-icform"><EmanetYon s={cekmece.satir} buyuk /></div>} />
      )}
    </>
  );
}

/* ─── Özet ───────────────────────────────────────────────────────────────── */
function OzetSekmesi({ ozet, yazar, hareketlereGit, emanetlereGit, yeniHareket }: {
  ozet: Ozet | null; yazar: boolean; hareketlereGit: (hesapId: number) => void; emanetlereGit: () => void; yeniHareket: (hesapId: number) => void;
}) {
  if (!ozet) return <div className="ccd-inline-note">Yükleniyor…</div>;
  const hs = ozet.hesaplar;
  const resmiKasa = paraToplam(hs.filter((h) => h.tur === 'KASA' && h.resmi));
  const gayriKasa = paraToplam(hs.filter((h) => h.tur === 'KASA' && !h.resmi));
  const banka = paraToplam(hs.filter((h) => h.tur === 'BANKA'));
  const gayriBanka = hs.filter((h) => h.tur === 'BANKA' && !h.resmi);
  const em: EmanetToplam = ozet.emanet.find((e) => e.para === 'TRY') ?? { para: 'TRY', bizdeKurus: 0, alacakKurus: 0, kisiAdet: 0 };
  const emDoviz = ozet.emanet.filter((e) => e.para !== 'TRY');
  const adet = (f: (h: Hesap) => boolean) => `${hs.filter(f).length} hesap`;

  return (
    <>
      <KpiIzgara tur="kasa-kpi" uclu={!ozet.gayriGorur}>
        <KpiKart ton="yesil" ikon="cuzdan" etiket={ozet.gayriGorur ? 'Resmî kasa' : 'Kasa'} fihrist="kasa-bakiyesi" deger={formatKisa(tl(resmiKasa))} tam={formatTl(tl(resmiKasa))}
                 soluk={tl(resmiKasa) === 0} alt={<><div>{adet((h) => h.tur === 'KASA' && h.resmi)}</div>{dovizSatiri(resmiKasa) && <div>{dovizSatiri(resmiKasa)}</div>}</>} />
        {ozet.gayriGorur && (
          <KpiKart ton="turuncu" ikon="cuzdan" etiket="Gayriresmî kasa" fihrist="resmi-gayriresmi-kasa" deger={formatKisa(tl(gayriKasa))} tam={formatTl(tl(gayriKasa))}
                   soluk={tl(gayriKasa) === 0} alt={<><div>{adet((h) => h.tur === 'KASA' && !h.resmi)} · muhasebe rolü görmez</div>{dovizSatiri(gayriKasa) && <div>{dovizSatiri(gayriKasa)}</div>}</>} />
        )}
        <KpiKart ton="mavi" ikon="depo" etiket="Banka" fihrist="banka-hesabi" deger={formatKisa(tl(banka))} tam={formatTl(tl(banka))} soluk={tl(banka) === 0}
                 alt={<><div>{adet((h) => h.tur === 'BANKA')}{gayriBanka.length > 0 ? ` · ${gayriBanka.length} tanesi gayriresmî` : ''}</div>{dovizSatiri(banka) && <div>{dovizSatiri(banka)}</div>}</>} />
        <KpiKart ton="mor" ikon="kisi" etiket="Bizde duran emanet" fihrist="emanet-bakiyesi" deger={formatKisa(em.bizdeKurus)} tam={formatTl(em.bizdeKurus)}
                 soluk={em.bizdeKurus === 0 && em.alacakKurus === 0}
                 alt={<>
                   <div>Bizim alacağımız: <b>{formatTl(em.alacakKurus)}</b></div>
                   {emDoviz.map((e) => <div key={e.para}>{e.para}: bizde {paraYaz(e.bizdeKurus, e.para)} · alacağımız {paraYaz(e.alacakKurus, e.para)}</div>)}
                   <div><button type="button" className="ccd-kasa-bag" onClick={emanetlereGit}>Kişi kişi gör →</button></div>
                 </>} />
      </KpiIzgara>

      {(['KASA', 'BANKA', 'EMANET'] as HesapTur[]).map((tur) => {
        const grup = hs.filter((h) => h.tur === tur);
        if (grup.length === 0) return null;
        const top = paraToplam(grup);
        return (
          <section key={tur} className="ccd-stack-sm" data-tur={tur === 'KASA' ? 'kasa-hesaplar' : undefined}>
            <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
              <div className="ccd-fieldset-title">{tur === 'KASA' ? 'Kasalar' : tur === 'BANKA' ? 'Banka hesapları' : 'Emanet hesapları'} <Ipucu id={tur === 'BANKA' ? 'banka-hesabi' : tur === 'EMANET' ? 'emanet-hesabi' : 'kasa-bakiyesi'} /></div>
              <div className="ccd-meta">Toplam <b>{top.map((x) => paraYaz(x.kurus, x.para)).join(' · ')}</b></div>
            </div>
            <div className="ccd-kasa-kartlar">
              {grup.map((h) => (
                <div key={h.id} className={`ccd-kasa-kart${h.resmi ? '' : ' ccd-kasa-kart-gayri'}${h.aktif ? '' : ' ccd-kasa-kart-pasif'}`}>
                  <div className="ccd-row-between" style={{ gap: 8, alignItems: 'flex-start' }}>
                    <div style={{ minWidth: 0 }}>
                      <div className="ccd-kasa-kart-ad" title={h.ad}>{h.ad}</div>
                      <div className="ccd-meta">{[h.bankaAdi, h.sube].filter(Boolean).join(' · ') || TUR_AD[h.tur]}</div>
                    </div>
                    <div className="ccd-kasa-rozetler">
                      {!h.resmi && <span className="ccd-badge ccd-badge-warning" data-fihrist="resmi-gayriresmi-kasa">gayriresmî</span>}
                      {h.para !== 'TRY' && <span className="ccd-badge">{h.para}</span>}
                      {!h.aktif && <span className="ccd-badge">pasif</span>}
                    </div>
                  </div>
                  <div className={`ccd-kasa-kart-bakiye${h.bakiyeKurus < 0 ? ' ccd-kasa-eksi' : ''}`} data-fihrist="kasa-bakiyesi">{paraYaz(h.bakiyeKurus, h.para)}</div>
                  {h.iban && <div className="ccd-meta ccd-mono ccd-kasa-iban">{ibanYaz(h.iban)}</div>}
                  <div className="ccd-meta">{h.sonHareket ? `Son hareket ${gunYaz(h.sonHareket)} · ${h.hareketAdet} hareket` : 'Henüz hareket yok'}</div>
                  {h.disHesapId && (
                    <div className="ccd-kasa-mutabakat" data-fihrist="mutabakat-farki">
                      <div><span>{ozet.muhasebeAd} bakiyesi</span><b>{h.disBakiyeKurus == null ? '—' : paraYaz(h.disBakiyeKurus, h.para)}</b></div>
                      <div>
                        <span>Fark <Ipucu id="mutabakat-farki" /></span>
                        <b className={h.farkKurus ? 'ccd-kasa-fark' : 'ccd-kasa-fark-yok'}>{h.farkKurus == null ? '—' : h.farkKurus === 0 ? 'yok ✓' : `${h.farkKurus > 0 ? '+' : '−'}${paraYaz(Math.abs(h.farkKurus), h.para)}`}</b>
                      </div>
                      {h.disBakiyeAt && <div className="ccd-meta">okundu: {formatTarih(h.disBakiyeAt)}</div>}
                    </div>
                  )}
                  <div className="ccd-row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 'auto' }}>
                    <button type="button" className="ccd-btn ccd-btn-sm" onClick={() => hareketlereGit(h.id)}>Hareketler</button>
                    {yazar && h.aktif && <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => yeniHareket(h.id)}>＋ Hareket</button>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </>
  );
}

/* ─── Hareketler ─────────────────────────────────────────────────────────── */
type HareketYanit = { satirlar: Hareket[]; toplam: number; sayfa: number; boyut: number; girisKurus: number; cikisKurus: number; paralar: string[]; hesap: Hesap | null };

function HareketSekmesi({ surum, hesaplar, kategoriler, yazar, ilkHesap, iptalAc }: {
  surum: number; hesaplar: Hesap[]; kategoriler: Kategori[]; yazar: boolean; ilkHesap: string; iptalAc: (h: Hareket) => void;
}) {
  const [hesap, setHesap] = useState(ilkHesap);
  const [kategori, setKategori] = useState('');
  const [yon, setYon] = useState<'' | 'GIRIS' | 'CIKIS'>('');
  const [bas, setBas] = useState('');
  const [bit, setBit] = useState('');
  const [metin, setMetin] = useState('');
  const [aranan, setAranan] = useState('');
  const [iptaller, setIptaller] = useState(true);
  const [sayfa, setSayfa] = useState(1);
  const [veri, setVeri] = useState<HareketYanit | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [iniyor, setIniyor] = useState(false);
  const sira = useRef(0);

  useEffect(() => { setHesap(ilkHesap); }, [ilkHesap]);
  // Arama kutusu: yazma bitince (350 ms) sorgulanır.
  useEffect(() => { const z = setTimeout(() => setAranan(metin.trim()), 350); return () => clearTimeout(z); }, [metin]);
  useEffect(() => { setSayfa(1); }, [hesap, kategori, yon, bas, bit, aranan, iptaller]);

  const sorgu = useMemo(() => {
    const p = new URLSearchParams();
    if (hesap) p.set('hesap', hesap);
    if (kategori) p.set('kategori', kategori);
    if (yon) p.set('yon', yon);
    if (bas) p.set('bas', bas);
    if (bit) p.set('bit', bit);
    if (aranan) p.set('ara', aranan);
    if (!iptaller) p.set('iptal', 'haric');
    return p.toString();
  }, [hesap, kategori, yon, bas, bit, aranan, iptaller]);

  useEffect(() => {
    const benim = ++sira.current;
    api<HareketYanit>(`/api/kasa/hareket?${sorgu}${sorgu ? '&' : ''}sayfa=${sayfa}`)
      .then((r) => { if (benim === sira.current) { setVeri(r); setHata(null); } })
      .catch((e) => { if (benim === sira.current) setHata((e as Error).message); });
  }, [sorgu, sayfa, surum]);

  const tekHesap = Boolean(hesap) && veri?.hesap != null;
  const tekPara = veri && veri.paralar.length === 1 ? veri.paralar[0] : null;
  const sayfaAdedi = veri ? Math.max(1, Math.ceil(veri.toplam / veri.boyut)) : 1;
  const suzgecVar = Boolean(hesap || kategori || yon || bas || bit || aranan || !iptaller);

  return (
    <>
      <div className="ccd-kasa-suzgec" data-tur="kasa-suzgec">
        <div className="ccd-field"><label className="ccd-field-label" htmlFor="kasa-f-hesap">Hesap</label>
          <AraSec id="kasa-f-hesap" secenekler={hesaplar.map(hesapSecenek)} deger={hesap} degis={setHesap} bosEtiket="— bütün hesaplar —" yerTutucu="Bütün hesaplar" /></div>
        <div className="ccd-field"><label className="ccd-field-label" htmlFor="kasa-f-kat">İşlem kategorisi <Ipucu id="islem-kategorisi" /></label>
          <AraSec id="kasa-f-kat" secenekler={kategoriler.map((k) => ({ id: k.id, ad: k.ad, alt: `${k.grup}${k.aktif ? '' : ' · pasif'}` }))} deger={kategori} degis={setKategori}
                  bosEtiket="— bütün kategoriler —" yerTutucu="Bütün kategoriler" azami={30} /></div>
        <div className="ccd-field"><span className="ccd-field-label">Yön</span>
          <Secim etiket="Yön" deger={yon} degis={setYon} secenekler={[['', 'Hepsi'], ['GIRIS', 'Giriş'], ['CIKIS', 'Çıkış']] as const} /></div>
        <div className="ccd-field ccd-kasa-suzgec-genis"><label className="ccd-field-label" htmlFor="kasa-f-bas">Tarih aralığı</label>
          <div className="ccd-kasa-aralik">
            <input id="kasa-f-bas" className="ccd-input" type="date" aria-label="Başlangıç tarihi" value={bas} max={bit || undefined} onChange={(e) => setBas(e.target.value)} />
            <input className="ccd-input" type="date" aria-label="Bitiş tarihi" value={bit} min={bas || undefined} onChange={(e) => setBit(e.target.value)} />
          </div></div>
        <div className="ccd-field"><label className="ccd-field-label" htmlFor="kasa-f-ara">Ara</label>
          <input id="kasa-f-ara" className="ccd-input" type="search" value={metin} onChange={(e) => setMetin(e.target.value)} placeholder="Cari, kişi, açıklama, belge no…" /></div>
      </div>

      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div className="ccd-meta" role="status">
          {!veri ? 'Yükleniyor…' : (
            <>
              <b>{veri.toplam}</b> hareket
              {tekPara
                ? <> · giriş <b>{paraYaz(veri.girisKurus, tekPara)}</b> · çıkış <b>{paraYaz(veri.cikisKurus, tekPara)}</b></>
                : veri.paralar.length > 1 && ' · listede birden çok para birimi var; toplam için hesap seçin'}
              {tekHesap && veri.hesap && <> · hesap bakiyesi <b data-fihrist="kasa-bakiyesi">{paraYaz(veri.hesap.bakiyeKurus, veri.hesap.para)}</b></>}
            </>
          )}
        </div>
        <div className="ccd-row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <label className="ccd-kasa-onay ccd-kasa-onay-kisa"><input type="checkbox" checked={iptaller} onChange={(e) => setIptaller(e.target.checked)} /><span>İptal edilenleri göster</span></label>
          {suzgecVar && <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => { setHesap(''); setKategori(''); setYon(''); setBas(''); setBit(''); setMetin(''); setIptaller(true); }}>Süzgeci temizle</button>}
          <button type="button" className="ccd-btn ccd-btn-sm" disabled={iniyor || !veri || veri.toplam === 0} title="Listeyi Excel'de açılacak dosya olarak indir (en çok 5.000 satır)"
                  onClick={() => { setIniyor(true); kasaCsvIndir(sorgu).catch((e) => setHata((e as Error).message)).finally(() => setIniyor(false)); }}>
            {iniyor ? 'Hazırlanıyor…' : "Excel'e aktar"}
          </button>
        </div>
      </div>
      {hata && <div className="ccd-banner ccd-banner-danger" role="alert"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span></div>}

      {veri && veri.satirlar.length === 0 ? (
        <div className="ccd-empty">{suzgecVar ? 'Bu süzgece uyan hareket yok.' : 'Henüz hareket girilmemiş. Sağ üstteki "＋ Yeni hareket" ile başlayın.'}</div>
      ) : veri && (
        <div className="ccd-table-wrap ccd-kasa-tablo" data-tur="kasa-hareketler">
          <table className="ccd-table">
            <thead><tr>
              <th className="ccd-th">Tarih</th>
              {!tekHesap && <th className="ccd-th">Hesap</th>}
              <th className="ccd-th">İşlem <Ipucu id="islem-kategorisi" /></th>
              <th className="ccd-th">Cari / kişi</th>
              <th className="ccd-th ccd-td-num">Giriş</th>
              <th className="ccd-th ccd-td-num">Çıkış</th>
              {tekHesap && <th className="ccd-th ccd-td-num">Bakiye <Ipucu id="yuruyen-bakiye" /></th>}
              <th className="ccd-th">Durum <Ipucu id="hareket-iptali" /></th>
            </tr></thead>
            <tbody>
              {veri.satirlar.map((h) => (
                <tr key={h.id} className={`ccd-tr${h.iptal ? ' ccd-kasa-iptal' : ''}`}>
                  <td className="ccd-td ccd-td-nowrap">{gunYaz(h.tarih)}</td>
                  {!tekHesap && (
                    <td className="ccd-td">
                      <button type="button" className="ccd-kasa-bag" onClick={() => setHesap(String(h.hesapId))} title="Yalnız bu hesabın hareketlerini göster">{h.hesapAd}</button>
                      {!h.hesapResmi && <div><span className="ccd-badge ccd-badge-warning">gayriresmî</span></div>}
                    </td>
                  )}
                  <td className="ccd-td ccd-kasa-islem">
                    <span className="ccd-kasa-ustu">{h.kategoriAd}</span>
                    {h.virmanId && <span className="ccd-badge" data-fihrist="virman">virman{h.karsiHesap ? ` ${h.yon === 'CIKIS' ? '→' : '←'} ${h.karsiHesap}` : ''}</span>}
                    {h.emanet && <span className="ccd-badge ccd-badge-accent" data-fihrist="emanet-bakiyesi">emanet</span>}
                    {(h.aciklama || h.belgeNo || h.vadeTarihi || h.orderNo) && (
                      <div className="ccd-meta">
                        {[h.aciklama, h.belgeNo && `belge ${h.belgeNo}`, h.vadeTarihi && `vade ${gunYaz(h.vadeTarihi)}`].filter(Boolean).join(' · ')}
                        {h.orderId && h.orderNo && <> {h.aciklama || h.belgeNo ? '· ' : ''}<Link to={`/siparis/${h.orderId}`}>sipariş {h.orderNo}</Link></>}
                      </div>
                    )}
                  </td>
                  <td className="ccd-td">
                    {h.customerId ? <Link to={`/crm/${h.customerId}`} title="Cari kartı">{h.cariAd}</Link> : h.cariAd ?? h.kisi ?? <span className="ccd-muted">—</span>}
                    {h.customerId == null && h.supplierId != null && <div className="ccd-meta">tedarikçi</div>}
                  </td>
                  <td className="ccd-td ccd-td-num ccd-kasa-giris"><span className="ccd-kasa-ustu">{h.yon === 'GIRIS' ? paraYaz(h.tutarKurus, h.para) : ''}</span></td>
                  <td className="ccd-td ccd-td-num ccd-kasa-cikis"><span className="ccd-kasa-ustu">{h.yon === 'CIKIS' ? paraYaz(h.tutarKurus, h.para) : ''}</span></td>
                  {tekHesap && <td className="ccd-td ccd-td-num">{h.iptal || h.yuruyenKurus == null ? <span className="ccd-muted">—</span> : <b className={h.yuruyenKurus < 0 ? 'ccd-kasa-eksi' : undefined}>{paraYaz(h.yuruyenKurus, h.para)}</b>}</td>}
                  <td className="ccd-td">
                    {h.iptal ? (
                      <div className="ccd-kasa-iptal-not">
                        <span className="ccd-badge ccd-badge-danger">iptal</span>
                        <div className="ccd-meta">{h.iptalNeden}{h.iptalEden ? ` · ${h.iptalEden}` : ''}{h.iptalAt ? ` · ${formatTarih(h.iptalAt)}` : ''}</div>
                      </div>
                    ) : (
                      <span className="ccd-row" style={{ gap: 6 }}>
                        <span className="ccd-meta" title={`Kaydeden: ${h.olusturan ?? 'sistem'} · ${formatTarih(h.createdAt)}`}>{h.olusturan?.split(' ')[0] ?? 'sistem'}</span>
                        {yazar && <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => iptalAc(h)}>İptal et</button>}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {tekHesap && veri.hesap && sayfa === sayfaAdedi && !kategori && !yon && !bas && !aranan && (
                <tr className="ccd-tr ccd-kasa-acilis">
                  <td className="ccd-td ccd-td-nowrap">{gunYaz(veri.hesap.acilisTarihi)}</td>
                  <td className="ccd-td" colSpan={4}>Açılış bakiyesi <Ipucu id="acilis-bakiyesi" /></td>
                  <td className="ccd-td ccd-td-num"><b>{paraYaz(veri.hesap.acilisKurus, veri.hesap.para)}</b></td>
                  <td className="ccd-td" />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {veri && sayfaAdedi > 1 && (
        <div className="ccd-row" style={{ gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button type="button" className="ccd-btn ccd-btn-sm" disabled={sayfa <= 1} onClick={() => setSayfa((n) => n - 1)}>← Daha yeni</button>
          <span className="ccd-meta">Sayfa {sayfa} / {sayfaAdedi}</span>
          <button type="button" className="ccd-btn ccd-btn-sm" disabled={sayfa >= sayfaAdedi} onClick={() => setSayfa((n) => n + 1)}>Daha eski →</button>
        </div>
      )}
    </>
  );
}

/* ─── Emanetler ──────────────────────────────────────────────────────────── */
/** Yön etiketi: artı → onların parası bizde; eksi → bizim paramız onlarda. */
function EmanetYon({ s, buyuk }: { s: EmanetSatiri; buyuk?: boolean }) {
  const tutar = paraYaz(Math.abs(s.netKurus), s.para);
  return (
    <span className={`ccd-kasa-yon ccd-kasa-yon-${s.yon === 'BIZDE' ? 'bizde' : s.yon === 'ALACAK' ? 'alacak' : 'kapali'}${buyuk ? ' ccd-kasa-yon-buyuk' : ''}`} data-fihrist="emanet-bakiyesi">
      {s.yon === 'KAPALI' ? 'kapandı · bakiye yok' : <><b>{tutar}</b> {s.yon === 'BIZDE' ? 'bizde duran' : 'bizim alacağımız'}</>}
    </span>
  );
}

function EmanetSekmesi({ surum, dokumAc, yeniHareket }: { surum: number; dokumAc: (s: EmanetSatiri) => void; yeniHareket?: (on: HareketOn) => void }) {
  const [veri, setVeri] = useState<{ satirlar: EmanetSatiri[]; toplam: EmanetToplam[] } | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [suz, setSuz] = useState<'ACIK' | 'BIZDE' | 'ALACAK' | 'KAPALI'>('ACIK');
  const [metin, setMetin] = useState('');
  useEffect(() => { api<{ satirlar: EmanetSatiri[]; toplam: EmanetToplam[] }>('/api/kasa/emanet').then(setVeri).catch((e) => setHata((e as Error).message)); }, [surum]);

  if (hata) return <div className="ccd-banner ccd-banner-danger" role="alert"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span></div>;
  if (!veri) return <div className="ccd-inline-note">Yükleniyor…</div>;
  const q = metin.trim().toLocaleLowerCase('tr');
  const liste = veri.satirlar.filter((s) => (suz === 'ACIK' ? s.yon !== 'KAPALI' : s.yon === suz) && (!q || s.ad.toLocaleLowerCase('tr').includes(q)));
  const say = (y: EmanetSatiri['yon']) => veri.satirlar.filter((s) => s.yon === y).length;

  return (
    <>
      <div className="ccd-meta">
        Emanet işaretli kategorilerle (kapora, avans, ortak parası…) girilen hareketlerden, kişi ve cari bazında hesaplanır.
        <b> Bizde duran</b>: başkasının parası sizde; geri verecek ya da mahsup edeceksiniz. <b>Bizim alacağımız</b>: sizin paranız başkasında; geri alacaksınız. <Ipucu id="emanet-bakiyesi" />
      </div>
      <div className="ccd-kasa-emanet-ozet">
        {(veri.toplam.length ? veri.toplam : [{ para: 'TRY' as Para, bizdeKurus: 0, alacakKurus: 0, kisiAdet: 0 }]).map((t) => (
          <div key={t.para} className="ccd-kasa-emanet-kutu" data-fihrist="emanet-bakiyesi">
            <div><span className="ccd-meta">Bizde duran{t.para !== 'TRY' ? ` (${t.para})` : ''}</span><b className="ccd-kasa-yon-bizde">{paraYaz(t.bizdeKurus, t.para)}</b></div>
            <div><span className="ccd-meta">Bizim alacağımız{t.para !== 'TRY' ? ` (${t.para})` : ''}</span><b className="ccd-kasa-yon-alacak">{paraYaz(t.alacakKurus, t.para)}</b></div>
          </div>
        ))}
      </div>
      <div className="ccd-row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <Secim etiket="Süzgeç" deger={suz} degis={setSuz}
               secenekler={[['ACIK', `Açık (${say('BIZDE') + say('ALACAK')})`], ['BIZDE', `Bizde duran (${say('BIZDE')})`], ['ALACAK', `Bizim alacağımız (${say('ALACAK')})`], ['KAPALI', `Kapananlar (${say('KAPALI')})`]] as const} />
        <input className="ccd-input ccd-kasa-ara" type="search" aria-label="Kişi ya da cari ara" value={metin} onChange={(e) => setMetin(e.target.value)} placeholder="Kişi ya da cari ara…" />
      </div>
      {liste.length === 0 ? (
        <div className="ccd-empty">
          {veri.satirlar.length === 0
            ? 'Henüz emanet hareketi yok. Kapora, avans ya da ortak parası girerken "emanet" işaretli bir kategori seçin; burada kişi kişi görünür.'
            : 'Bu süzgece uyan emanet yok.'}
        </div>
      ) : (
        <div className="ccd-table-wrap ccd-kasa-tablo">
          <table className="ccd-table">
            <thead><tr>
              <th className="ccd-th">Kim</th>
              <th className="ccd-th ccd-td-num">Aldığımız</th>
              <th className="ccd-th ccd-td-num">Verdiğimiz</th>
              <th className="ccd-th">Bakiye <Ipucu id="emanet-bakiyesi" /></th>
              <th className="ccd-th">Son hareket</th>
              <th className="ccd-th">İşlem</th>
            </tr></thead>
            <tbody>
              {liste.map((s) => (
                <tr key={`${s.anahtar}|${s.para}`} className="ccd-tr">
                  <td className="ccd-td">
                    {s.customerId ? <Link to={`/crm/${s.customerId}`} title="Cari kartı"><b>{s.ad}</b></Link> : <b>{s.ad}</b>}
                    <div className="ccd-meta">{s.tur === 'MUSTERI' ? 'müşteri' : s.tur === 'TEDARIKCI' ? 'tedarikçi' : 'kişi'}{s.para !== 'TRY' ? ` · ${s.para}` : ''}</div>
                  </td>
                  <td className="ccd-td ccd-td-num">{paraYaz(s.girisKurus, s.para)}</td>
                  <td className="ccd-td ccd-td-num">{paraYaz(s.cikisKurus, s.para)}</td>
                  <td className="ccd-td"><EmanetYon s={s} /></td>
                  <td className="ccd-td ccd-td-nowrap">{gunYaz(s.sonTarih)}<div className="ccd-meta">{s.adet} hareket</div></td>
                  <td className="ccd-td ccd-td-nowrap">
                    <span className="ccd-row" style={{ gap: 6 }}>
                      <button type="button" className="ccd-btn ccd-btn-sm" onClick={() => dokumAc(s)}>Döküm</button>
                      {yeniHareket && s.yon !== 'KAPALI' && (
                        <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm"
                                title={s.yon === 'BIZDE' ? 'İade ya da mahsup: çıkış hareketi gir' : 'Geri ödeme alındı: giriş hareketi gir'}
                                onClick={() => yeniHareket({
                                  yon: s.yon === 'BIZDE' ? 'CIKIS' : 'GIRIS',
                                  ...(s.customerId ? { customerId: s.customerId } : s.supplierId ? { supplierId: s.supplierId } : { kisi: s.ad }),
                                })}>
                          {s.yon === 'BIZDE' ? 'İade / mahsup' : 'Geri alındı'}
                        </button>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ─── Tanımlar ───────────────────────────────────────────────────────────── */
function TanimSekmesi({ ozet, hesaplar, kategoriler, yukleniyor, hesapAc, kategoriAc, oldu, hataVer }: {
  ozet: Ozet | null; hesaplar: Hesap[]; kategoriler: Kategori[]; yukleniyor: boolean;
  hesapAc: (h: Hesap | null) => void; kategoriAc: (k: Kategori | null) => void; oldu: (mesaj: string) => void; hataVer: (m: string | null) => void;
}) {
  const { can } = useAuth();
  const yazar = can('cash.settings');
  const [mesgul, setMesgul] = useState<string | null>(null);
  const muhasebe = ozet?.muhasebeAd ?? 'Muhasebe programı';
  const bagliProgram = ozet != null && ozet.muhasebeKip !== 'off';

  async function iceAl() {
    if (!window.confirm(`${muhasebe}'teki kasa ve banka hesapları panelde tanım olarak açılsın mı?\n\nZaten ilişkili olanlar yinelenmez. ${muhasebe}'e hiçbir şey yazılmaz.`)) return;
    setMesgul('ice-al'); hataVer(null);
    try {
      const r = await api<{ acilan: string[]; iliskilendirilen: string[]; atlanan: { ad: string; neden: string }[]; zatenVar: number }>('/api/kasa/hesap/ice-al', { method: 'POST' });
      oldu([
        r.acilan.length ? `${r.acilan.length} hesap açıldı (${r.acilan.join(', ')})` : 'Yeni hesap açılmadı',
        r.iliskilendirilen.length ? `${r.iliskilendirilen.length} mevcut hesap ilişkilendirildi` : null,
        r.zatenVar ? `${r.zatenVar} hesap zaten ilişkiliydi` : null,
        r.atlanan.length ? `atlanan: ${r.atlanan.map((a) => `${a.ad} — ${a.neden}`).join('; ')}` : null,
      ].filter(Boolean).join(' · ') + '.');
    } catch (e) { hataVer(e instanceof Error ? e.message : 'İçe alınamadı'); }
    finally { setMesgul(null); }
  }
  async function tazele() {
    setMesgul('tazele'); hataVer(null);
    try {
      const r = await api<{ guncellenen: number; bulunamayan: string[] }>('/api/kasa/dis-bakiye-tazele', { method: 'POST' });
      oldu(`${r.guncellenen} hesabın ${muhasebe} bakiyesi okundu.${r.bulunamayan.length ? ` ${muhasebe}'te bulunamayan: ${r.bulunamayan.join(', ')}.` : ''}`);
    } catch (e) { hataVer(e instanceof Error ? e.message : 'Bakiyeler okunamadı'); }
    finally { setMesgul(null); }
  }
  async function kategoriYaz(k: Kategori, govde: Record<string, unknown>, mesaj: string) {
    setMesgul(`k${k.id}`); hataVer(null);
    try { await api(`/api/kasa/kategori/${k.id}`, { method: 'PUT', body: JSON.stringify(govde) }); oldu(mesaj); }
    catch (e) { hataVer(e instanceof Error ? e.message : 'Kategori güncellenemedi'); }
    finally { setMesgul(null); }
  }
  async function kategoriSil(k: Kategori) {
    if (!window.confirm(`"${k.ad}" kategorisi silinsin mi? Hiç kullanılmadığı için silinebilir.`)) return;
    setMesgul(`k${k.id}`); hataVer(null);
    try { await api(`/api/kasa/kategori/${k.id}`, { method: 'DELETE' }); oldu(`"${k.ad}" silindi.`); }
    catch (e) { hataVer(e instanceof Error ? e.message : 'Kategori silinemedi'); }
    finally { setMesgul(null); }
  }
  /** Sıralama: komşusuyla sıra numarasını değiştirir (aynı grup içinde). */
  async function tasi(grup: Kategori[], n: number, yon: -1 | 1) {
    const a = grup[n], b = grup[n + yon];
    if (!a || !b) return;
    setMesgul(`k${a.id}`); hataVer(null);
    try {
      const [sa, sb] = a.sira === b.sira ? [b.sira + yon, b.sira] : [b.sira, a.sira];
      await api(`/api/kasa/kategori/${a.id}`, { method: 'PUT', body: JSON.stringify({ sira: Math.max(0, sa) }) });
      await api(`/api/kasa/kategori/${b.id}`, { method: 'PUT', body: JSON.stringify({ sira: Math.max(0, sb) }) });
      oldu('Sıra değişti.');
    } catch (e) { hataVer(e instanceof Error ? e.message : 'Sıra değiştirilemedi'); }
    finally { setMesgul(null); }
  }

  const gruplar = useMemo(() => {
    const m = new Map<string, Kategori[]>();
    for (const k of [...kategoriler].sort((x, y) => x.sira - y.sira || x.id - y.id)) m.set(k.grup, [...(m.get(k.grup) ?? []), k]);
    return [...m.entries()];
  }, [kategoriler]);

  if (yukleniyor) return <div className="ccd-inline-note">Yükleniyor…</div>;
  const bagliVar = hesaplar.some((h) => h.disHesapId);

  return (
    <>
      <div className="ccd-card ccd-stack-sm" data-tur="kasa-tanim-hesap" id="kasa-hesaplar">
        <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
          <div style={{ minWidth: 0 }}>
            <div className="ccd-fieldset-title">Hesaplar</div>
            <div className="ccd-meta">
              Paranın durduğu yerler: kasa, banka hesabı, emanet hesabı.{ozet?.gayriGorur ? ' Gayriresmî işaretli hesabı muhasebe rolü hiçbir yerde görmez.' : ''}
              {bagliProgram ? ` ${muhasebe}'teki hesapla ilişkilendirirseniz iki bakiye yan yana görünür.` : ` ${muhasebe} bağlı değil; hesaplar elle tanımlanır.`}
            </div>
          </div>
          {yazar && (
            <div className="ccd-row" style={{ gap: 8, flexWrap: 'wrap' }}>
              {bagliProgram && bagliVar && can('cash.write') && <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" disabled={mesgul != null} onClick={() => void tazele()} title={`${muhasebe}'teki bakiyeleri şimdi oku (yalnız okur)`}>{mesgul === 'tazele' ? 'Okunuyor…' : 'Bakiyeleri tazele'}</button>}
              {bagliProgram && <button type="button" className="ccd-btn ccd-btn-sm" disabled={mesgul != null} onClick={() => void iceAl()} title={`${muhasebe}'teki kasa ve banka hesaplarını tek tıkla tanım olarak açar; var olanı yinelemez`}>{mesgul === 'ice-al' ? 'İçe alınıyor…' : 'Hesapları içe al'}</button>}
              <button type="button" className="ccd-btn ccd-btn-primary ccd-btn-sm" onClick={() => hesapAc(null)}>＋ Hesap tanımla</button>
            </div>
          )}
        </div>
        {hesaplar.length === 0 ? (
          <div className="ccd-empty">Henüz hesap yok. {yazar ? '"＋ Hesap tanımla" ile ilk kasanızı açın' + (bagliProgram ? ` ya da "Hesapları içe al" ile ${muhasebe}'teki hesapları getirin.` : '.') : 'Yöneticinizden tanımlamasını isteyin.'}</div>
        ) : (
          <div className="ccd-table-wrap ccd-kasa-tablo ccd-kasa-tablo-ic">
            <table className="ccd-table">
              <thead><tr>
                <th className="ccd-th">Hesap</th><th className="ccd-th">Tür</th>
                <th className="ccd-th">Banka bilgisi <Ipucu id="banka-hesabi" /></th>
                <th className="ccd-th ccd-td-num">Açılış <Ipucu id="acilis-bakiyesi" /></th>
                <th className="ccd-th ccd-td-num">Bakiye <Ipucu id="kasa-bakiyesi" /></th>
                <th className="ccd-th">{muhasebe} <Ipucu id="mutabakat-farki" /></th>
                {yazar && <th className="ccd-th">İşlem</th>}
              </tr></thead>
              <tbody>
                {hesaplar.map((h) => (
                  <tr key={h.id} className={`ccd-tr${h.aktif ? '' : ' ccd-kasa-pasif'}`}>
                    <td className="ccd-td"><b>{h.ad}</b>{h.not && <div className="ccd-meta">{h.not}</div>}</td>
                    <td className="ccd-td">
                      {TUR_AD[h.tur]}
                      <div className="ccd-kasa-rozetler">
                        {!h.resmi && <span className="ccd-badge ccd-badge-warning">gayriresmî</span>}
                        {h.para !== 'TRY' && <span className="ccd-badge">{h.para}</span>}
                        {!h.aktif && <span className="ccd-badge">pasif</span>}
                      </div>
                    </td>
                    <td className="ccd-td">{[h.bankaAdi, h.sube].filter(Boolean).join(' · ') || <span className="ccd-muted">—</span>}{h.iban && <div className="ccd-meta ccd-mono ccd-kasa-iban">{ibanYaz(h.iban)}</div>}</td>
                    <td className="ccd-td ccd-td-num">{paraYaz(h.acilisKurus, h.para)}<div className="ccd-meta">{h.acilisTarihi ? gunYaz(h.acilisTarihi) : 'tarihsiz'}</div></td>
                    <td className="ccd-td ccd-td-num"><b>{paraYaz(h.bakiyeKurus, h.para)}</b><div className="ccd-meta">{h.hareketAdet} hareket</div></td>
                    <td className="ccd-td">
                      {h.disHesapId ? (
                        <>
                          <span className="ccd-badge ccd-badge-success">ilişkili</span>
                          <div className="ccd-meta">{h.disBakiyeKurus == null ? 'bakiye okunmadı' : paraYaz(h.disBakiyeKurus, h.para)}{h.farkKurus ? ` · fark ${h.farkKurus > 0 ? '+' : '−'}${paraYaz(Math.abs(h.farkKurus), h.para)}` : h.farkKurus === 0 ? ' · fark yok' : ''}</div>
                        </>
                      ) : <span className="ccd-muted">—</span>}
                    </td>
                    {yazar && <td className="ccd-td"><button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => hesapAc(h)}>Düzenle</button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="ccd-card ccd-stack-sm" data-tur="kasa-tanim-kategori" id="kasa-kategoriler">
        <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
          <div style={{ minWidth: 0 }}>
            <div className="ccd-fieldset-title">İşlem kategorileri <Ipucu id="islem-kategorisi" /></div>
            <div className="ccd-meta">Her hareket bir kategoriyle girilir: paranın neden girdiğini ya da çıktığını söyler. Kendi kategorinizi ekleyin, adını değiştirin, kullanmadığınızı pasife alın. Kullanılmış kategori silinmez.</div>
          </div>
          {yazar && <button type="button" className="ccd-btn ccd-btn-primary ccd-btn-sm" onClick={() => kategoriAc(null)}>＋ Kategori ekle</button>}
        </div>
        <div className="ccd-table-wrap ccd-kasa-tablo ccd-kasa-tablo-uzun">
          <table className="ccd-table">
            <thead><tr>
              <th className="ccd-th">Kategori</th><th className="ccd-th">Yön</th>
              <th className="ccd-th">Emanet <Ipucu id="emanet-bakiyesi" /></th>
              <th className="ccd-th ccd-td-num">Kullanım</th>
              {yazar && <th className="ccd-th">Sıra</th>}
              {yazar && <th className="ccd-th">İşlem</th>}
            </tr></thead>
            <tbody>
              {gruplar.flatMap(([grup, liste]) => [
                <tr key={`g-${grup}`} className="ccd-vade-grup"><td colSpan={yazar ? 6 : 4}>{grup} · {liste.length} kategori</td></tr>,
                ...liste.map((k, n) => (
                  <tr key={k.id} className={`ccd-tr${k.aktif ? '' : ' ccd-kasa-pasif'}`}>
                    <td className="ccd-td">
                      <b>{k.ad}</b>
                      <div className="ccd-kasa-rozetler">
                        {k.sistem && <span className="ccd-badge" title="Panelin kendi kullandığı kategori; silinmez">sistem</span>}
                        {!k.sistem && k.kod && <span className="ccd-badge" title="Panelin kendi yazdığı tahsilat kayıtları bu kategoriyi kullanır; silinmez">otomatik kayıtlar</span>}
                        {k.cariZorunlu && <span className="ccd-badge">cari zorunlu</span>}
                        {!k.aktif && <span className="ccd-badge">pasif</span>}
                      </div>
                    </td>
                    <td className="ccd-td">{YON_AD[k.yon]}</td>
                    <td className="ccd-td">{k.emanet ? <span className="ccd-badge ccd-badge-accent">emanet</span> : <span className="ccd-muted">—</span>}</td>
                    <td className="ccd-td ccd-td-num">{k.kullanim}</td>
                    {yazar && (
                      <td className="ccd-td ccd-td-nowrap">
                        <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" disabled={n === 0 || mesgul != null} aria-label={`${k.ad}: yukarı taşı`} title="Yukarı taşı" onClick={() => void tasi(liste, n, -1)}>↑</button>
                        <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" disabled={n === liste.length - 1 || mesgul != null} aria-label={`${k.ad}: aşağı taşı`} title="Aşağı taşı" onClick={() => void tasi(liste, n, 1)}>↓</button>
                      </td>
                    )}
                    {yazar && (
                      <td className="ccd-td ccd-td-nowrap">
                        <span className="ccd-row" style={{ gap: 6 }}>
                          <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => kategoriAc(k)}>Düzenle</button>
                          {!k.kod && (
                            <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" disabled={mesgul != null}
                                    onClick={() => void kategoriYaz(k, { aktif: !k.aktif }, `"${k.ad}" ${k.aktif ? 'pasife alındı' : 'yeniden etkin'}.`)}>
                              {k.aktif ? 'Pasife al' : 'Etkinleştir'}
                            </button>
                          )}
                          {!k.kod && k.kullanim === 0 && <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" style={{ color: 'var(--ccd-danger)' }} disabled={mesgul != null} onClick={() => void kategoriSil(k)}>Sil</button>}
                        </span>
                      </td>
                    )}
                  </tr>
                )),
              ])}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
